import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { ClipboardList, CalendarDays, Flag, MoreHorizontal, ChefHat, BookOpen, LineChart, Sparkles, Users, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// Phone navigation: four thumb-reachable tabs with everything else in a sheet.
// Replaces the wrapped two-row tab strip on small screens.
export default function BottomNav({ hasRace }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { planTier, hasTracker, isCoach } = useAuth();
  const [more, setMore] = useState(false);

  useEffect(() => { setMore(false); }, [pathname]);

  const primary = [
    { to: "/app", label: "Plan", icon: ClipboardList, testid: "bottomnav-plan" },
    { to: "/tracker", label: "Tracker", icon: CalendarDays, testid: "bottomnav-tracker",
      dot: !hasTracker },
    ...(hasRace
      ? [{ to: "/race", label: "Race", icon: Flag, testid: "bottomnav-race" }]
      : [{ to: "/insights", label: "Insights", icon: LineChart, testid: "bottomnav-insights" }]),
  ];

  const rest = [
    ...(planTier !== "lite" ? [{ to: "/recipes", label: "Recipes", icon: ChefHat, hint: "Meals that fit your macros", testid: "bottomnav-more-recipes" }] : []),
    { to: "/learn", label: "Guide", icon: BookOpen, hint: "How to fuel, explained simply", testid: "bottomnav-more-guide" },
    ...(hasRace ? [{ to: "/insights", label: "Insights", icon: LineChart, hint: "Trends, streaks and PBs", testid: "bottomnav-more-insights" }] : []),
    { to: "/ask-ai", label: "Ask AI", icon: Sparkles, hint: "Any nutrition question, answered", testid: "bottomnav-more-ask" },
    ...(isCoach ? [{ to: "/coach", label: "Athletes", icon: Users, hint: "Race plans for the athletes you coach", testid: "bottomnav-more-athletes" }] : []),
  ];

  const isActive = (to) => pathname === to || pathname.startsWith(`${to}/`);
  const moreActive = rest.some((r) => isActive(r.to));

  const tabClass = (active) =>
    `relative flex-1 flex flex-col items-center justify-center gap-0.5 py-2 transition-colors ${
      active ? "text-[#E2E30A]" : "text-zinc-400 active:text-white"}`;

  return createPortal(
    <>
      {more && (
        <div className="sm:hidden fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex flex-col justify-end"
          onClick={() => setMore(false)} data-testid="bottomnav-more-backdrop">
          <div className="bg-[#111110] border-t border-white/15 max-h-[70vh] overflow-y-auto mb-[calc(4.25rem+env(safe-area-inset-bottom))]"
            onClick={(e) => e.stopPropagation()} data-testid="bottomnav-more-sheet">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-400 font-bold">Everything else</p>
              <button type="button" onClick={() => setMore(false)} data-testid="bottomnav-more-close"
                className="p-1 text-zinc-400 hover:text-white"><X className="h-4 w-4" /></button>
            </div>
            <div className="divide-y divide-white/[0.06]">
              {rest.map(({ to, label, icon: Icon, hint, testid }) => (
                <button key={to} type="button" data-testid={testid}
                  onClick={() => { setMore(false); navigate(to); }}
                  className={`w-full flex items-center gap-3 px-4 py-3.5 text-left active:bg-white/[0.04] ${isActive(to) ? "text-[#E2E30A]" : "text-white"}`}>
                  <Icon className="h-5 w-5 shrink-0" strokeWidth={2.2} />
                  <span className="min-w-0">
                    <span className="block text-[12px] font-bold uppercase tracking-[0.16em]">{label}</span>
                    <span className="block text-[11px] text-zinc-400 mt-0.5">{hint}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <nav data-testid="bottom-nav" data-swipe-ignore
        className="sm:hidden fixed bottom-0 inset-x-0 z-50 flex items-stretch bg-[#0A0A09]/95 backdrop-blur border-t border-white/12 pb-[env(safe-area-inset-bottom)]">
        {primary.map(({ to, label, icon: Icon, testid, dot }) => (
          <button key={to} type="button" data-testid={testid} onClick={() => navigate(to)}
            className={tabClass(isActive(to))}>
            {isActive(to) && <span className="absolute top-0 h-[2px] w-10 bg-[#E2E30A]" />}
            <Icon className="h-5 w-5" strokeWidth={2.2} />
            <span className="text-[9px] font-bold uppercase tracking-[0.12em]">{label}</span>
            {dot && <span className="absolute top-1.5 right-[22%] h-1.5 w-1.5 rounded-full bg-[#E2E30A]" />}
          </button>
        ))}
        <button type="button" data-testid="bottomnav-more" onClick={() => setMore((v) => !v)}
          className={tabClass(more || moreActive)}>
          {moreActive && !more && <span className="absolute top-0 h-[2px] w-10 bg-[#E2E30A]" />}
          <MoreHorizontal className="h-5 w-5" strokeWidth={2.2} />
          <span className="text-[9px] font-bold uppercase tracking-[0.12em]">More</span>
        </button>
      </nav>
    </>,
    document.body
  );
}
